import {
  API_LABEL,
  REGIONS,
  type ApiKind,
  type VolumeTrend,
  type ChangeLogEntry,
  type Partner,
  type PartnerActivityEntry,
  type PartnerFlag,
  type PartnerFlagType,
  type RegionKey,
  type Tier,
  type TierId,
} from "./model";
import type { MetricsPortfolio } from "./metrics.types";
import { STUB_PORTFOLIO } from "./metrics.fixtures";

/**
 * Data access for the Metrics Dashboard: one `fetch<Thing>` function plus a
 * wire -> domain mapper (anti-corruption layer), matching how insights-home
 * structures a feature's `.api.ts`.
 *
 * TODAY: `fetchMetricsPortfolio` resolves the stub portfolio, which is already
 * in domain shape, so it doesn't go through the mapper. `toMetricsPortfolio`
 * and the `Raw*` types below are the real contract for the backend and are
 * unit-tested (`metrics.api.test.ts`).
 *
 * WHEN `customer-portal-services` EXISTS, `fetchMetricsPortfolio` becomes:
 *
 *   const token = await getAccessTokenSilently();
 *   const { data } = await axios.get<RawMetricsPortfolio>(
 *     `${apiBaseUrl}/api/metrics/portfolio`,
 *     { headers: { Authorization: `Bearer ${token}` } },
 *   );
 *   return toMetricsPortfolio(data);
 *
 * (add `apiBaseUrl` per `docs/operations/runtime-configuration.md`; add `axios`;
 * `twosion-ui/src/api/*` is the reference once `@tveyes/customer-portal-schema`
 * ships and `Raw*` can be replaced by generated `components["schemas"]` types.)
 * Then delete `metrics.fixtures.ts` from the bundle (keep it for tests).
 */

/* ---- Wire shape — what the backend is expected to send. snake_case, nullable,
   loose string unions. Deliberately NOT the domain type. ---- */

interface RawApiUsage {
  used: number;
  cap: number;
}

interface RawPartner {
  id: string;
  name: string;
  initials: string;
  partner_id: string;
  tier_id: string;
  account_manager: string;
  acv: number | null;
  since: string;
  renewal_date: string;
  flag: { label: string; type: string } | null;
  usage: {
    saved: RawApiUsage;
    adhoc: RawApiUsage;
    firehose_enabled: boolean;
    media_player_views_30d: number;
    call_volume_series: number[];
    volume_trend: { saved: (number | null)[]; adhoc: (number | null)[] };
    recent_activity: RawActivityEntry[];
  };
}

interface RawActivityEntry {
  time: string;
  type: string;
  query: string;
  region: string;
  result_count: number | null;
  status: string;
}

interface RawTier {
  id: string;
  name: string;
  saved_cap: number;
  adhoc_cap: number;
  query_len: number;
  regions: Record<string, boolean>;
}

interface RawChangeLogEntry {
  time: string;
  partner: string;
  field: string;
  from: string;
  to: string;
  by: string;
}

export interface RawMetricsPortfolio {
  partners: RawPartner[];
  tiers: RawTier[];
  change_log: RawChangeLogEntry[];
}

/* ---- Mapper ---- */

const TIER_IDS: readonly TierId[] = ["starter", "growth", "enterprise"];
const FLAG_TYPES: readonly PartnerFlagType[] = ["success", "warning", "error"];

function toTierId(value: string, context: string): TierId {
  if ((TIER_IDS as readonly string[]).includes(value)) return value as TierId;
  throw new Error(`Unknown tier id "${value}" (${context})`);
}

function toFlag(raw: RawPartner["flag"]): PartnerFlag {
  if (!raw) return { label: "Healthy", type: "success" };
  const type = (FLAG_TYPES as readonly string[]).includes(raw.type)
    ? (raw.type as PartnerFlagType)
    : "warning";
  return { label: raw.label, type };
}

/**
 * Wire activity rows with an unrecognized `type`/`region` are dropped rather
 * than thrown on — one noisy log row shouldn't fail the whole portfolio load
 * the way an unknown tier id does.
 */
function toActivity(raw: RawActivityEntry[]): PartnerActivityEntry[] {
  return raw
    .filter(
      (entry): entry is RawActivityEntry & { type: ApiKind; region: RegionKey } =>
        Object.hasOwn(API_LABEL, entry.type) &&
        (REGIONS as readonly string[]).includes(entry.region)
    )
    .map((entry) => ({
      time: entry.time,
      type: entry.type,
      query: entry.query,
      region: entry.region,
      resultCount: entry.result_count,
      status: entry.status === "Error" ? "Error" : "Hit",
    }));
}

/** Pairs the series by day, up to the longer one; missing-value policy: overview D5. */
function toVolumeTrend(raw: RawPartner["usage"]["volume_trend"]): VolumeTrend {
  const length = Math.max(raw.saved.length, raw.adhoc.length);
  return Array.from({ length }, (_, index) => ({
    day: index + 1,
    saved: raw.saved[index] ?? null,
    adhoc: raw.adhoc[index] ?? null,
  }));
}

function toPartner(raw: RawPartner): Partner {
  return {
    id: raw.id,
    name: raw.name,
    initials: raw.initials,
    partnerId: raw.partner_id,
    tierId: toTierId(raw.tier_id, `partner ${raw.id}`),
    accountManager: raw.account_manager,
    acv: raw.acv ?? 0,
    since: raw.since,
    renewalDate: raw.renewal_date,
    flag: toFlag(raw.flag),
    usage: {
      saved: raw.usage.saved,
      adhoc: raw.usage.adhoc,
      firehoseEnabled: raw.usage.firehose_enabled,
      mediaPlayerViews30d: raw.usage.media_player_views_30d,
      callVolumeSeries: raw.usage.call_volume_series,
      volumeTrend: toVolumeTrend(raw.usage.volume_trend),
      recentActivity: toActivity(raw.usage.recent_activity),
    },
  };
}

function toTier(raw: RawTier): Tier {
  return {
    id: toTierId(raw.id, "tier"),
    name: raw.name,
    savedCap: raw.saved_cap,
    adhocCap: raw.adhoc_cap,
    queryLen: raw.query_len,
    regions: raw.regions as Tier["regions"],
  };
}

function toChangeLogEntry(raw: RawChangeLogEntry): ChangeLogEntry {
  return {
    time: raw.time,
    partner: raw.partner,
    field: raw.field,
    from: raw.from,
    to: raw.to,
    by: raw.by,
  };
}

/** Translate the wire payload into the domain `MetricsPortfolio`. */
export function toMetricsPortfolio(raw: RawMetricsPortfolio): MetricsPortfolio {
  return {
    partners: raw.partners.map(toPartner),
    tiers: raw.tiers.map(toTier),
    changeLog: raw.change_log.map(toChangeLogEntry),
  };
}

/** Small delay so loading states are exercised in dev, mirroring insights-home's mockDelay. */
const STUB_LATENCY_MS = 250;

export async function fetchMetricsPortfolio(): Promise<MetricsPortfolio> {
  await new Promise((resolve) => setTimeout(resolve, STUB_LATENCY_MS));
  return STUB_PORTFOLIO;
}
